<template>
  <div>
    <AccordionBase
      text="Текст"
      :showChevronWithActions="true"
      :classContent="[style.InputStyles]"
      :class="[style.block, style.contentBlock]"
    >
      <template #actions>
        <div />
      </template>

      <InputStyle
        description="Заголовок"
        placeholder="Введите заголовок"
        size="M"
        textarea
        :modelValue="questionTitle"
        @update:modelValue="setQuestionTitle"
      />

      <InputStyle
        description="Описание"
        placeholder="Введите описание"
        size="M"
        textarea
        :modelValue="questionDescription"
        @update:modelValue="setQuestionDescription"
      />

      <InputStyle
        description="Текст под кнопкой"
        placeholder="Введите текст"
        size="M"
        :modelValue="textBellowButton"
        @update:modelValue="setTextBellowButton"
      />
    </AccordionBase>

    <AccordionBase
      text="Варианты ответа"
      :showChevronWithActions="true"
      :classContent="[style.fields]"
      :classHeader="[style.variantsHeader]"
      :class="style.block"
    >
      <template #actions>
        <ButtonIcon
          variant="ghost"
          size="28"
          :icon="addIcon"
          :class="style.addButton"
          @click.stop="addVariant"
        />
      </template>

      <div
        v-for="(variant, variantIndex) in normalizedVariants"
        :key="variant._key"
      >
        <input
          :ref="
            (element) =>
              setVariantFileInputRef(element as Element, variantIndex)
          "
          type="file"
          accept="image/*"
          hidden
          @change="setVariantImage(variantIndex, $event)"
        />

        <InputFieldMedia
          :class="style.input"
          placeholder="Введите текст"
          :hasImage="hasImageQuestion"
          :image="getVariantImage(variantIndex)"
          :modelValue="variant.text || ''"
          :actions="getVariantActions(variantIndex)"
          @upload="openVariantFileDialog(variantIndex)"
          @update:modelValue="setVariantText(variantIndex, $event)"
        />
      </div>
    </AccordionBase>
  </div>
</template>

<script lang="ts">
import {defineComponent, PropType, markRaw} from "vue";

import style from "../../QuestionPanelComponent.module.scss";

import AccordionBase from "@/elements/UI/accordion/AccordionBase.vue";
import GhostButton from "@/elements/UI/button/ButtonStyle/Default/ghost/GhostButton.vue";
import InputStyle from "@/elements/UI/input/Style/InputStyle.vue";
import InputFieldMedia from "@/elements/UI/input/Media/InputFieldMedia.vue";

import addIcon from "$images/icons/button/Name=Add, Size=16, Tone=Default.svg";
import chevronUpIcon from "$images/icons/button/Name=Chevron-up, Size=16, Tone=Default.svg";
import chevronDownIcon from "$images/icons/button/Name=Chevron-down, Size=16, Tone=Default.svg";
import deleteIcon from "$images/icons/button/Name=Delete, Size=16, Tone=Error.svg";

import {questions, QuestionTypes, QuestionVariant} from "@/page/quiz/typeQuiz";
import {DOMAIN} from "@/http";
import ButtonIcon from "@/elements/UI/button/ButtonIcon/ButtonIcon.vue";
import GhostButtonError from "@/elements/UI/button/ButtonStyle/Error/ghost/GhostButtonError.vue";
import {modifyQuiz} from "@/page/quiz/scriptQuiz";

type VariantImagePreviewCache = Record<string, string>;

interface Window {
  __readdsVariantImagePreviewUrls?: VariantImagePreviewCache;
}

function getVariantImagePreviewCache(): VariantImagePreviewCache {
  const win = window as Window;

  if (typeof win === "undefined") {
    return {};
  }

  if (!win.__readdsVariantImagePreviewUrls) {
    win.__readdsVariantImagePreviewUrls = {};
  }

  return win.__readdsVariantImagePreviewUrls;
}

export default defineComponent({
  name: "OpenListQuestionContent",

  components: {
    ButtonIcon,
    AccordionBase,
    GhostButton,
    InputStyle,
    InputFieldMedia
  },

  props: {
    question: {
      type: Object as PropType<questions | null>,
      default: null
    },

    questionIndex: {
      type: Number,
      default: -1
    }
  },

  data() {
    return {
      style,
      addIcon,

      variantFileInputRefs: {} as Record<number, HTMLInputElement | null>,
      variantPreviewUrls: {} as Record<string, string>
    };
  },

  mounted() {
    this.ensureVariantKeys();
  },

  beforeUnmount() {
    this.variantPreviewUrls = {};
  },

  methods: {
    createVariantKey(variantIndex: number): string {
      return `open_list_${this.resolvedQuestionIndex}_${variantIndex}_${Date.now()}_${Math.random()
        .toString(16)
        .slice(2)}`;
    },

    ensureVariantKeys(): void {
      if (!this.question) {
        return;
      }

      if (!Array.isArray(this.question.variants)) {
        this.question.variants = [];
        return;
      }

      const hasVariantWithoutKey = this.question.variants.some(
        (variant: QuestionVariant) => !variant._key
      );

      if (!hasVariantWithoutKey) {
        return;
      }

      this.setVariants(this.normalizedVariants);
    },

    setQuestionTitle(value: string): void {
      if (!this.question) {
        return;
      }

      this.question.question = value;
    },

    setQuestionDescription(value: string): void {
      if (!this.question) {
        return;
      }

      this.ensureContacts();

      this.question.contacts!.paragraph = value;
    },

    addVariant(): void {
      const variants = [
        ...this.normalizedVariants,
        {
          text: "",
          img: "",
          _key: this.createVariantKey(this.normalizedVariants.length)
        }
      ];

      this.setVariants(variants);
    },

    setVariantText(variantIndex: number, value: string): void {
      if (!this.question || !Array.isArray(this.question.variants)) {
        return;
      }

      const variant = this.question.variants[variantIndex];

      if (!variant) {
        return;
      }

      if (!variant._key) {
        variant._key = this.createVariantKey(variantIndex);
      }

      variant.text = value;
    },

    moveVariantUp(variantIndex: number): void {
      if (variantIndex <= 0) {
        return;
      }

      const variants = [...this.normalizedVariants];

      [variants[variantIndex - 1], variants[variantIndex]] = [
        variants[variantIndex],
        variants[variantIndex - 1]
      ];

      this.setVariants(this.reindexLocalVariantImageFiles(variants));
    },

    moveVariantDown(variantIndex: number): void {
      if (variantIndex >= this.normalizedVariants.length - 1) {
        return;
      }

      const variants = [...this.normalizedVariants];

      [variants[variantIndex + 1], variants[variantIndex]] = [
        variants[variantIndex],
        variants[variantIndex + 1]
      ];

      this.setVariants(this.reindexLocalVariantImageFiles(variants));
    },

    deleteVariant(variantIndex: number): void {
      const variants = [...this.normalizedVariants];
      const variant = variants[variantIndex];

      if (variant?.img?.startsWith("blob:")) {
        URL.revokeObjectURL(variant.img);
      }

      if (variant?.img) {
        this.revokeVariantPreview(variant.img);
      }

      variants.splice(variantIndex, 1);

      this.setVariants(this.reindexLocalVariantImageFiles(variants));
    },

    setVariants(variants: QuestionVariant[]): void {
      if (!this.question) {
        return;
      }

      this.question.variants = variants.map((variant, variantIndex) => {
        return {
          text: variant.text || "",
          img: variant.img || "",
          _key: variant._key || this.createVariantKey(variantIndex)
        };
      });
    },

    getVariantImage(variantIndex: number): string {
      const imagePath = this.normalizedVariants[variantIndex]?.img || "";

      if (!imagePath) {
        return "";
      }

      if (
        imagePath.startsWith("blob:") ||
        imagePath.startsWith("data:") ||
        imagePath.startsWith("http://") ||
        imagePath.startsWith("https://")
      ) {
        return `url('${imagePath}')`;
      }

      const cachedPreview = this.getCachedVariantPreview(imagePath);

      if (cachedPreview) {
        return `url('${cachedPreview}')`;
      }

      return `url('https://${DOMAIN}/api/img?name=${encodeURIComponent(imagePath)}')`;
    },

    getCachedVariantPreview(fileName: string): string {
      if (!fileName) {
        return "";
      }

      const cachedFileName = this.getFileNameFromPath(fileName);
      const sharedPreview = this.getSharedVariantPreview(cachedFileName);

      if (sharedPreview) {
        return sharedPreview;
      }

      const cachedFile = this.getCachedVariantFile(cachedFileName);

      if (!cachedFile) {
        return "";
      }

      const currentPreviewUrl =
        this.variantPreviewUrls[fileName] ||
        this.variantPreviewUrls[cachedFileName];

      if (currentPreviewUrl) {
        this.setSharedVariantPreview(cachedFileName, currentPreviewUrl);

        return currentPreviewUrl;
      }

      const previewUrl = URL.createObjectURL(cachedFile);

      this.variantPreviewUrls = {
        ...this.variantPreviewUrls,
        [fileName]: previewUrl,
        [cachedFileName]: previewUrl
      };

      this.setSharedVariantPreview(cachedFileName, previewUrl);

      return previewUrl;
    },

    revokeVariantPreview(fileName: string): void {
      if (!fileName) {
        return;
      }

      const cachedFileName = this.getFileNameFromPath(fileName);
      const previewCache = getVariantImagePreviewCache();
      const previewUrl =
        previewCache[fileName] ||
        previewCache[cachedFileName] ||
        this.variantPreviewUrls[fileName] ||
        this.variantPreviewUrls[cachedFileName];

      if (previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }

      const updatedPreviewUrls = {...this.variantPreviewUrls};

      delete updatedPreviewUrls[fileName];
      delete updatedPreviewUrls[cachedFileName];
      delete previewCache[fileName];
      delete previewCache[cachedFileName];

      this.variantPreviewUrls = updatedPreviewUrls;
    },

    revokeAllVariantPreviews(): void {
      this.variantPreviewUrls = {};
    },

    setVariantFileInputRef(
      element: Element | null,
      variantIndex: number
    ): void {
      this.variantFileInputRefs[variantIndex] =
        element instanceof HTMLInputElement ? element : null;
    },

    openVariantFileDialog(variantIndex: number): void {
      const input = this.variantFileInputRefs[variantIndex];

      if (!input) {
        return;
      }

      input.value = "";
      input.click();
    },

    getFileExtension(file: File): string {
      const fileExtension = file.name.includes(".")
        ? file.name.split(".").pop()?.toLowerCase()
        : "";
      const mimeExtension = file.type.split("/").pop()?.toLowerCase();

      return fileExtension || mimeExtension || "jpg";
    },

    getFileNameFromPath(path: string): string {
      if (!path) {
        return "";
      }

      if (path.startsWith("http://") || path.startsWith("https://")) {
        try {
          const url = new URL(path);
          const name = url.searchParams.get("name");

          if (name) {
            return this.getFileNameFromPath(name);
          }

          return url.pathname.split("/").pop() || path;
        } catch {
          return path.split("/").pop() || path;
        }
      }

      return path.split("/").pop() || path;
    },

    getSharedVariantPreview(fileName: string): string {
      if (!fileName) {
        return "";
      }

      const cachedFileName = this.getFileNameFromPath(fileName);
      const previewCache = getVariantImagePreviewCache();

      return previewCache[fileName] || previewCache[cachedFileName] || "";
    },

    setSharedVariantPreview(fileName: string, previewUrl: string): void {
      if (!fileName || !previewUrl) {
        return;
      }

      const cachedFileName = this.getFileNameFromPath(fileName);
      const previewCache = getVariantImagePreviewCache();
      const oldPreviewUrl = previewCache[cachedFileName];

      if (
        oldPreviewUrl &&
        oldPreviewUrl !== previewUrl &&
        oldPreviewUrl.startsWith("blob:")
      ) {
        URL.revokeObjectURL(oldPreviewUrl);
      }

      previewCache[fileName] = previewUrl;
      previewCache[cachedFileName] = previewUrl;

      this.variantPreviewUrls = {
        ...this.variantPreviewUrls,
        [fileName]: previewUrl,
        [cachedFileName]: previewUrl
      };
    },

    getCachedVariantFile(fileName: string): File | null {
      const cachedFileName = this.getFileNameFromPath(fileName);

      return (
        this.cachedFiles.find((file: File) => {
          return file.name === fileName || file.name === cachedFileName;
        }) || null
      );
    },

    isCurrentQuestionVariantImageName(fileName: string): boolean {
      const cachedFileName = this.getFileNameFromPath(fileName);
      const variantImageNameRegExp = new RegExp(
        `^\\d+-${this.resolvedQuestionIndex}-variant\\.[^/.]+$`
      );

      return variantImageNameRegExp.test(cachedFileName);
    },

    createVariantImageNameByIndex(
      imagePath: string,
      variantIndex: number
    ): string {
      const fileName = this.getFileNameFromPath(imagePath);
      const extension = fileName.includes(".")
        ? fileName.split(".").pop() || "jpg"
        : "jpg";

      return `${variantIndex}-${this.resolvedQuestionIndex}-variant.${extension}`;
    },

    reindexLocalVariantImageFiles(
      variants: Required<QuestionVariant>[]
    ): Required<QuestionVariant>[] {
      const imageFileNames = variants
        .map((variant) => this.getFileNameFromPath(variant.img || ""))
        .filter((fileName) => {
          return (
            Boolean(fileName) &&
            this.isCurrentQuestionVariantImageName(fileName)
          );
        });

      if (imageFileNames.length === 0) {
        return variants;
      }

      const canSafelyReindexAllImages = imageFileNames.every((fileName) => {
        return Boolean(this.getCachedVariantFile(fileName));
      });

      if (!canSafelyReindexAllImages) {
        return variants;
      }

      const changes: Array<{
        oldFileName: string;
        newFileName: string;
        cachedFile: File;
        previewUrl: string;
      }> = [];
      const fileNamesToReplace = new Set<string>();

      const variantsWithUpdatedImages = variants.map(
        (variant, variantIndex) => {
          const oldFileName = this.getFileNameFromPath(variant.img || "");

          if (
            !oldFileName ||
            !this.isCurrentQuestionVariantImageName(oldFileName)
          ) {
            return variant;
          }

          const cachedFile = this.getCachedVariantFile(oldFileName);

          if (!cachedFile) {
            return variant;
          }

          const newFileName = this.createVariantImageNameByIndex(
            oldFileName,
            variantIndex
          );
          const previewUrl =
            this.getSharedVariantPreview(oldFileName) ||
            this.variantPreviewUrls[oldFileName] ||
            "";

          changes.push({
            oldFileName,
            newFileName,
            cachedFile,
            previewUrl
          });
          fileNamesToReplace.add(oldFileName);
          fileNamesToReplace.add(newFileName);

          return {
            ...variant,
            img: newFileName
          };
        }
      );

      if (changes.length === 0) {
        return variantsWithUpdatedImages;
      }

      for (let index = this.cachedFiles.length - 1; index >= 0; index -= 1) {
        if (fileNamesToReplace.has(this.cachedFiles[index].name)) {
          this.cachedFiles.splice(index, 1);
        }
      }

      const previewCache = getVariantImagePreviewCache();
      const updatedVariantPreviewUrls = {...this.variantPreviewUrls};

      fileNamesToReplace.forEach((fileName) => {
        delete previewCache[fileName];
        delete updatedVariantPreviewUrls[fileName];
      });

      changes.forEach(({newFileName, cachedFile, previewUrl}) => {
        const renamedFile = new File([cachedFile], newFileName, {
          type: cachedFile.type,
          lastModified: cachedFile.lastModified
        });

        this.cachedFiles.push(renamedFile);

        if (previewUrl) {
          previewCache[newFileName] = previewUrl;
          updatedVariantPreviewUrls[newFileName] = previewUrl;
        }
      });

      this.variantPreviewUrls = updatedVariantPreviewUrls;

      return variantsWithUpdatedImages;
    },

    createVariantImageFileName(file: File, variantIndex: number): string {
      return `${variantIndex}-${this.resolvedQuestionIndex}-variant.${this.getFileExtension(file)}`;
    },

    addCachedVariantFile(file: File): void {
      const fileIndex = this.cachedFiles.findIndex((cachedFile: File) => {
        return cachedFile.name === file.name;
      });

      if (fileIndex !== -1) {
        this.cachedFiles.splice(fileIndex, 1, file);
        return;
      }

      this.cachedFiles.push(file);
    },

    removeCachedVariantFile(imagePath: string): void {
      if (!imagePath) {
        return;
      }

      const fileName = this.getFileNameFromPath(imagePath);
      const fileIndex = this.cachedFiles.findIndex((file: File) => {
        return file.name === imagePath || file.name === fileName;
      });

      if (fileIndex === -1) {
        return;
      }

      this.cachedFiles.splice(fileIndex, 1);
    },

    setVariantImage(variantIndex: number, event: Event): void {
      const input = event.target as HTMLInputElement | null;
      const file = input?.files?.[0];

      if (!file) {
        return;
      }

      if (!file.type.startsWith("image/")) {
        if (input) {
          input.value = "";
        }

        return;
      }

      const variants = [...this.normalizedVariants];

      if (!variants[variantIndex]) {
        if (input) {
          input.value = "";
        }

        return;
      }

      const oldImage = variants[variantIndex].img || "";

      if (oldImage.startsWith("blob:")) {
        URL.revokeObjectURL(oldImage);
      }

      if (oldImage) {
        this.revokeVariantPreview(oldImage);
        this.removeCachedVariantFile(oldImage);
      }

      const newFileName = this.createVariantImageFileName(file, variantIndex);
      const newFile = new File([file], newFileName, {
        type: file.type,
        lastModified: file.lastModified
      });
      const previewUrl = URL.createObjectURL(newFile);

      this.revokeVariantPreview(newFileName);
      this.addCachedVariantFile(newFile);

      this.setSharedVariantPreview(newFileName, previewUrl);

      variants[variantIndex] = {
        ...variants[variantIndex],
        img: newFile.name
      };

      this.setVariants(variants);

      if (input) {
        input.value = "";
      }
    },

    getVariantActions(variantIndex: number) {
      const actions: ListActionItem[] = [
        {
          text: "Переместить вверх",
          icon: chevronUpIcon,
          disabled: variantIndex <= 0,
          variant: "button",
          handler: () => this.moveVariantUp(variantIndex)
        },
        {
          text: "Переместить вниз",
          icon: chevronDownIcon,
          disabled: variantIndex >= this.normalizedVariants.length - 1,
          variant: "button",
          handler: () => this.moveVariantDown(variantIndex)
        }
      ];

      actions.push({
        text: "Удалить",
        icon: deleteIcon,
        variant: "button",
        buttonComponent: markRaw(GhostButtonError),
        handler: () => this.deleteVariant(variantIndex)
      });

      return actions;
    },

    ensureContacts(): void {
      if (!this.question) {
        return;
      }

      if (!this.question.contacts) {
        this.question.contacts = {};
      }
    },

    setTextBellowButton(value: string): void {
      if (!this.question) {
        return;
      }

      modifyQuiz("set", value, ["textBellowButton"]);
    }
  },

  computed: {
    quiz() {
      return this.$quizStore.getQuiz;
    },

    resolvedQuestionIndex(): number {
      if (this.questionIndex >= 0) {
        return this.questionIndex;
      }

      const selectedQuestion = this.$quizStore.getSelectedQuestion;

      if (typeof selectedQuestion === "number" && selectedQuestion >= 0) {
        return selectedQuestion;
      }

      const quizQuestions = this.quiz?.questions;

      if (!this.question || !Array.isArray(quizQuestions)) {
        return 0;
      }

      const currentQuestionIndex = quizQuestions.findIndex(
        (question: questions) => question === this.question
      );

      return currentQuestionIndex >= 0 ? currentQuestionIndex : 0;
    },

    cachedFiles(): File[] {
      return this.$quizStore.getCachedFiles || [];
    },

    questionTitle(): string {
      return this.question?.question || "";
    },

    questionDescription(): string {
      return this.question?.contacts?.paragraph || "";
    },

    variants(): QuestionVariant[] {
      if (!this.question || !Array.isArray(this.question.variants)) {
        return [];
      }

      return this.question.variants as QuestionVariant[];
    },

    normalizedVariants(): Required<QuestionVariant>[] {
      return this.variants.map((variant, variantIndex) => {
        return {
          text: variant.text || "",
          img: variant.img || "",
          _key: variant._key || this.createVariantKey(variantIndex)
        };
      });
    },

    hasImageQuestion(): boolean {
      const questionTypes = ["radio-img", "checkbox-img"] as QuestionTypes[];

      if (!this.question?.type) {
        return false;
      }

      return questionTypes.includes(this.question.type);
    },

    textBellowButton(): string {
      return modifyQuiz("get", "", ["textBellowButton"]);
    }
  },

  watch: {
    cachedFiles: {
      handler(): void {
        this.$forceUpdate();
      },
      deep: true
    },

    questionIndex(): void {
      this.variantPreviewUrls = {};
      this.variantFileInputRefs = {};
    }
  }
});
</script>
